import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Truck } from 'lucide-react';
import SinaContextualCue from '@/components/SinaAssistant/SinaContextualCue.jsx';
import { readSinaHidden, SINA_VISIBILITY_EVENT } from '@/components/SinaAssistant/sinaPreferences.js';
import { buildOverloadMessage, computeCargoBoxes, computeVehicleLoad, resolveVehicleShape } from '@/pages/Repartos/vehicleLoad.js';

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const BOX_WIDTH = 14;
const BOX_HEIGHT = 12;
const BOX_STAGGER_MS = 28;
const MAX_STAGGER_MS = 700;
// Below this viewport height (e.g. 1366x768) the gauge starts compact.
const COMPACT_VIEWPORT_HEIGHT = 800;

// Cargo areas of each silhouette (viewBox 0 -8 240 120) and the roof line
// where overflow boxes are stacked when the load exceeds the capacity.
const SHAPES = {
  camion: { roof: 10, cargo: [{ x: 8, y: 14, width: 150, height: 66 }] },
  semi: { roof: 14, cargo: [{ x: 4, y: 18, width: 166, height: 62 }] },
  acoplado: {
    roof: 16,
    cargo: [
      { x: 6, y: 20, width: 88, height: 58 },
      { x: 114, y: 20, width: 66, height: 58 },
    ],
  },
  utilitario: { roof: 12, cargo: [{ x: 14, y: 26, width: 128, height: 52 }] },
  moto: { roof: 20, cargo: [{ x: 56, y: 24, width: 70, height: 42 }] },
};

const ANIMATION_CSS = `
.vlg-box { transform-box: fill-box; transform-origin: center bottom; animation: vlg-drop 460ms cubic-bezier(0.22, 1, 0.36, 1) both; }
.vlg-box--over { animation: vlg-drop 460ms cubic-bezier(0.22, 1, 0.36, 1) both, vlg-teeter 1.6s ease-in-out 480ms 2; }
@keyframes vlg-drop { from { transform: translateY(-36px); opacity: 0; } 60% { opacity: 1; } 82% { transform: translateY(1.5px) scaleY(0.94); } to { transform: none; } }
@keyframes vlg-teeter { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-5deg); } 75% { transform: rotate(5deg); } }
@media (prefers-reduced-motion: reduce) { .vlg-box, .vlg-box--over { animation: none; } }
`;

// Slots are ordered like a real load: bottom row first, from the cab wall
// (right) towards the rear doors, so the truck visibly fills up to the roof.
function buildSlots(shape) {
  const slots = [];
  SHAPES[shape].cargo.forEach((area) => {
    const cols = Math.max(Math.floor(area.width / BOX_WIDTH), 1);
    const rows = Math.max(Math.floor(area.height / BOX_HEIGHT), 1);
    const cellWidth = area.width / cols;
    const cellHeight = area.height / rows;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        slots.push({
          x: area.x + col * cellWidth,
          y: area.y + area.height - (row + 1) * cellHeight,
          width: cellWidth,
          height: cellHeight,
          row,
          col,
        });
      }
    }
  });
  return slots.sort((a, b) => a.row - b.row || b.x - a.x);
}

function buildOverflowSlots(shape, slots) {
  const bottomRow = slots.filter((slot) => slot.row === 0);
  const { roof } = SHAPES[shape];
  return bottomRow.map((slot) => ({ ...slot, y: roof - slot.height - 2 }));
}

function getTone(ratio) {
  if (ratio > 1) return 'danger';
  if (ratio >= 0.85) return 'warning';
  return 'success';
}

function readStoredExpanded(storageKey) {
  try {
    const stored = window.localStorage.getItem(`sinamon.vehicleLoadGauge.${storageKey}`);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // Storage can be unavailable (private mode); fall back to the viewport.
  }
  return typeof window === 'undefined' ? true : window.innerHeight >= COMPACT_VIEWPORT_HEIGHT;
}

function storeExpanded(storageKey, expanded) {
  try {
    window.localStorage.setItem(`sinamon.vehicleLoadGauge.${storageKey}`, expanded ? '1' : '0');
  } catch {
    // Preference is a convenience only.
  }
}

function useSinaHidden() {
  const [hidden, setHidden] = useState(() => readSinaHidden());
  useEffect(() => {
    const sync = () => setHidden(readSinaHidden());
    window.addEventListener(SINA_VISIBILITY_EVENT, sync);
    return () => window.removeEventListener(SINA_VISIBILITY_EVENT, sync);
  }, []);
  return hidden;
}

function CargoBox({ slot, delay, color, overflow }) {
  const inset = 1.1;
  const x = slot.x + inset;
  const y = slot.y + inset;
  const width = slot.width - inset * 2;
  const height = slot.height - inset * 2;
  return (
    <g className={overflow ? 'vlg-box vlg-box--over' : 'vlg-box'} style={{ animationDelay: `${delay}ms` }}>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="1.6"
        fill={color}
        fillOpacity={(slot.row + slot.col) % 2 === 0 ? 0.92 : 0.74}
        stroke="rgba(0,0,0,0.28)"
        strokeWidth="0.8"
      />
      <path d={`M${x + width / 2} ${y}v${height}`} stroke="rgba(255,255,255,0.35)" strokeWidth="1.4" />
    </g>
  );
}

function VehicleSilhouette({ shape, ratio, tone, compact = false }) {
  const uid = useId();
  const slots = buildSlots(shape);
  const overflowSlots = buildOverflowSlots(shape, slots);
  const { filled, overflow } = computeCargoBoxes(ratio, slots.length, overflowSlots.length);
  const previousFilled = useRef(filled);
  const previousOverflow = useRef(overflow);
  const color = `rgb(var(--${tone}))`;
  const danger = 'rgb(var(--danger))';
  const body = 'rgba(var(--fg),0.72)';
  const soft = 'rgba(var(--subpanel),0.9)';
  const glass = 'rgba(var(--brand),0.28)';
  const wheel = 'rgb(var(--panel))';

  // Only newly added boxes animate; the stagger starts from the previous count.
  const fillBase = filled > previousFilled.current ? previousFilled.current : filled;
  const overflowBase = overflow > previousOverflow.current ? previousOverflow.current : overflow;
  useEffect(() => {
    previousFilled.current = filled;
    previousOverflow.current = overflow;
  }, [filled, overflow]);
  const fillStagger = Math.min(BOX_STAGGER_MS, MAX_STAGGER_MS / Math.max(filled - fillBase, 1));
  const overflowStart = Math.min((filled - fillBase) * fillStagger, MAX_STAGGER_MS);

  return (
    <svg
      viewBox="0 -8 240 120"
      className="h-auto w-full overflow-visible"
      role="img"
      aria-label={`Ocupación de la unidad ${Math.round(ratio * 100)}%`}
    >
      {compact ? null : <style>{ANIMATION_CSS}</style>}
      <defs>
        <clipPath id={`${uid}-cargo`}>
          {SHAPES[shape].cargo.map((area) => (
            <rect key={`${area.x}-${area.y}`} x={area.x} y={area.y} width={area.width} height={area.height} rx="4" />
          ))}
        </clipPath>
      </defs>

      {shape === 'camion' ? (
        <g>
          <rect x="4" y="10" width="158" height="74" rx="7" fill={soft} stroke={body} strokeWidth="3" />
          <path d="M166 34h38l22 24v26h-60z" fill={soft} stroke={body} strokeWidth="3" strokeLinejoin="round" />
          <path d="M174 41h26l14 15h-40z" fill={glass} stroke={body} strokeWidth="2" />
          <rect x="4" y="84" width="222" height="6" rx="3" fill={body} />
        </g>
      ) : null}
      {shape === 'semi' ? (
        <g>
          <rect x="0" y="14" width="174" height="70" rx="6" fill={soft} stroke={body} strokeWidth="3" />
          <path d="M180 30h32l22 26v28h-54z" fill={soft} stroke={body} strokeWidth="3" strokeLinejoin="round" />
          <path d="M187 37h22l16 17h-38z" fill={glass} stroke={body} strokeWidth="2" />
          <rect x="0" y="84" width="234" height="6" rx="3" fill={body} />
        </g>
      ) : null}
      {shape === 'acoplado' ? (
        <g>
          <rect x="2" y="16" width="96" height="66" rx="5" fill={soft} stroke={body} strokeWidth="3" />
          <path d="M98 86h14" stroke={body} strokeWidth="3" strokeLinecap="round" />
          <rect x="110" y="16" width="74" height="66" rx="5" fill={soft} stroke={body} strokeWidth="3" />
          <path d="M188 34h24l22 24v26h-46z" fill={soft} stroke={body} strokeWidth="3" strokeLinejoin="round" />
          <path d="M194 41h16l14 15h-30z" fill={glass} stroke={body} strokeWidth="2" />
          <rect x="2" y="82" width="96" height="6" rx="3" fill={body} />
          <rect x="110" y="84" width="124" height="6" rx="3" fill={body} />
        </g>
      ) : null}
      {shape === 'utilitario' ? (
        <g>
          <path d="M8 22a10 10 0 0 1 10-10h150c12 0 20 6 28 18l22 30v24H8z" fill={soft} stroke={body} strokeWidth="3" strokeLinejoin="round" />
          <path d="M156 22h12c8 0 13 4 18 11l12 17h-42z" fill={glass} stroke={body} strokeWidth="2" />
          <path d="M148 18v66" stroke={body} strokeWidth="2" />
        </g>
      ) : null}
      {shape === 'moto' ? (
        <g>
          <rect x="52" y="20" width="78" height="50" rx="8" fill={soft} stroke={body} strokeWidth="3" />
          <path d="M60 86l40-14h50l22-26h16" fill="none" stroke={body} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M150 72l26 14" stroke={body} strokeWidth="5" strokeLinecap="round" />
        </g>
      ) : null}

      <g clipPath={`url(#${uid}-cargo)`}>
        {slots.slice(0, filled).map((slot, index) => (
          <CargoBox
            key={`box-${index}`}
            slot={slot}
            color={color}
            delay={index >= fillBase ? Math.round((index - fillBase) * fillStagger) : 0}
          />
        ))}
      </g>
      {overflowSlots.slice(0, overflow).map((slot, index) => (
        <CargoBox
          key={`over-${index}`}
          slot={slot}
          color={danger}
          overflow
          delay={index >= overflowBase ? overflowStart + (index - overflowBase) * 60 : 0}
        />
      ))}

      {shape === 'moto' ? (
        <>
          <circle cx="58" cy="90" r="16" fill={wheel} stroke={body} strokeWidth="5" />
          <circle cx="182" cy="90" r="16" fill={wheel} stroke={body} strokeWidth="5" />
        </>
      ) : null}
      {shape === 'acoplado' ? (
        <>
          <circle cx="24" cy="94" r="12" fill={wheel} stroke={body} strokeWidth="5" />
          <circle cx="76" cy="94" r="12" fill={wheel} stroke={body} strokeWidth="5" />
          <circle cx="136" cy="96" r="12" fill={wheel} stroke={body} strokeWidth="5" />
          <circle cx="212" cy="96" r="12" fill={wheel} stroke={body} strokeWidth="5" />
        </>
      ) : null}
      {shape !== 'moto' && shape !== 'acoplado' ? (
        <>
          <circle cx={shape === 'semi' ? 34 : 44} cy="94" r="13" fill={wheel} stroke={body} strokeWidth="5" />
          {shape === 'semi' ? <circle cx="66" cy="94" r="13" fill={wheel} stroke={body} strokeWidth="5" /> : null}
          <circle cx={shape === 'utilitario' ? 182 : 196} cy="94" r="13" fill={wheel} stroke={body} strokeWidth="5" />
        </>
      ) : null}
    </svg>
  );
}

function CapacityBar({ label, used, capacity, ratio, unit, digits }) {
  if (!capacity) {
    return (
      <div className="text-[11px] text-[rgba(var(--fg),0.56)]">
        {label}: {formatNumber(used, digits)} {unit} · sin capacidad cargada en la unidad
      </div>
    );
  }
  const tone = getTone(ratio);
  const available = capacity - used;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.52)]">{label}</span>
        <span className="tabular-nums text-[rgba(var(--fg),0.72)]">
          {formatNumber(used, digits)} / {formatNumber(capacity, digits)} {unit}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[rgba(var(--border),0.3)]">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.min(ratio, 1) * 100}%`, background: `rgb(var(--${tone}))` }}
        />
      </div>
      <div className={`mt-0.5 text-[11px] tabular-nums ${available < 0 ? 'font-semibold text-[rgb(var(--danger))]' : 'text-[rgba(var(--fg),0.6)]'}`}>
        {available < 0 ? `Excedido por ${formatNumber(-available, digits)} ${unit}` : `Disponible ${formatNumber(available, digits)} ${unit}`}
      </div>
    </div>
  );
}

function ToggleButton({ expanded, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      title={expanded ? 'Compactar el indicador de carga' : 'Ver la carga de la unidad'}
      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-lg border border-[rgba(var(--border),0.45)] px-2 text-[11px] font-medium text-[rgba(var(--fg),0.72)] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
    >
      {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      {expanded ? 'Compactar' : 'Ver carga'}
    </button>
  );
}

export default function VehicleLoadGauge({
  unidad,
  totals,
  missingVolumeCount = 0,
  fallbackShape = 'camion',
  layout = 'column',
  storageKey = 'default',
}) {
  const [expanded, setExpanded] = useState(() => readStoredExpanded(storageKey));
  const sinaHidden = useSinaHidden();
  const toggle = () => {
    setExpanded((prev) => {
      storeExpanded(storageKey, !prev);
      return !prev;
    });
  };

  if (!unidad) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-[rgba(var(--border),0.5)] px-3 py-2.5 text-[12px] text-[rgba(var(--fg),0.6)]">
        <Truck size={18} className="shrink-0" />
        Elija una unidad para ver la capacidad disponible y cómo se va llenando.
      </div>
    );
  }

  const shape = resolveVehicleShape(unidad.tipo, fallbackShape);
  const load = computeVehicleLoad(unidad, totals);
  const tone = getTone(load.ratio);
  const percent = Math.round(load.ratio * 100);
  const title = unidad.patente || `Unidad ${unidad.id}`;
  const subtitle = [unidad.tipo, unidad.marca, unidad.modelo].filter(Boolean).join(' · ');
  // Sina reacts only when the load goes over capacity; users who hid Sina
  // keep the plain alert.
  const overloadAlert = load.overloaded && !sinaHidden ? (
    <SinaContextualCue state="warning" title="Te pasaste de la capacidad" message={buildOverloadMessage(load)} />
  ) : load.overloaded ? (
    <div className="rounded-xl border border-[rgba(var(--danger),0.4)] bg-[rgba(var(--danger),0.1)] px-2.5 py-1.5 text-[12px] font-medium text-[rgb(var(--fg))]">
      La carga supera la capacidad de la unidad.
    </div>
  ) : null;

  if (!expanded) {
    return (
      <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.44)] px-2.5 py-1.5">
        <div className="flex items-center gap-2.5">
          <div className="w-[64px] shrink-0">
            <VehicleSilhouette shape={shape} ratio={load.ratio} tone={tone} compact />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[12px] font-semibold text-[rgb(var(--fg))]">{title}</span>
              {load.hasCapacity ? (
                <span className={`text-[13px] font-semibold tabular-nums text-[rgb(var(--${tone}))]`}>{percent}%</span>
              ) : (
                <span className="text-[11px] text-[rgba(var(--fg),0.56)]">Sin capacidad</span>
              )}
            </div>
            {load.hasCapacity ? (
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[rgba(var(--border),0.3)]">
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${Math.min(load.ratio, 1) * 100}%`, background: `rgb(var(--${tone}))` }}
                />
              </div>
            ) : null}
            {load.overloaded ? (
              <div className="mt-0.5 text-[11px] font-semibold text-[rgb(var(--danger))]">La carga supera la capacidad</div>
            ) : null}
          </div>
          <ToggleButton expanded={false} onToggle={toggle} />
        </div>
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-[rgb(var(--fg))]">{title}</div>
        <div className="truncate text-[11px] text-[rgba(var(--fg),0.6)]">{subtitle}</div>
      </div>
      <div className="flex items-center gap-2">
        {load.hasCapacity ? (
          <div className={`text-right text-xl font-semibold tabular-nums text-[rgb(var(--${tone}))]`}>{percent}%</div>
        ) : null}
        <ToggleButton expanded onToggle={toggle} />
      </div>
    </div>
  );
  const details = (
    <>
      <div className="space-y-2">
        <CapacityBar label="Volumen" used={load.usedM3} capacity={load.capacityM3} ratio={load.m3Ratio || 0} unit="m³" digits={2} />
        <CapacityBar label="Peso" used={load.usedKg} capacity={load.capacityKg} ratio={load.kgRatio || 0} unit="kg" digits={0} />
      </div>
      {missingVolumeCount > 0 ? (
        <div className="mt-2 text-[11px] text-[rgba(var(--fg),0.58)]">
          {missingVolumeCount === 1 ? '1 guía no informa m³' : `${missingVolumeCount} guías no informan m³`}; el volumen puede ser mayor.
        </div>
      ) : null}
      {overloadAlert ? <div className="mt-2">{overloadAlert}</div> : null}
    </>
  );

  if (layout === 'row') {
    return (
      <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.44)] px-3 py-2.5">
        <div className="grid items-center gap-3 sm:grid-cols-[minmax(160px,240px)_minmax(0,1fr)]">
          <div className="mx-auto w-full max-w-[240px]">
            <VehicleSilhouette shape={shape} ratio={load.ratio} tone={tone} />
          </div>
          <div className="min-w-0">
            {header}
            <div className="mt-2">{details}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.44)] px-3 py-3">
      {header}
      <div className="mx-auto mt-2 max-w-[260px]">
        <VehicleSilhouette shape={shape} ratio={load.ratio} tone={tone} />
      </div>
      <div className="mt-2">{details}</div>
    </div>
  );
}
